<template>
  <div class="supplier-coupon-buy-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('supplierCouponBuy.listTitle')" :subtitle="$t('supplierCouponBuy.page.subtitle')" />

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.buyId" clearable
            :placeholder="$t('supplierCouponBuy.placeholder.buyId')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.couponId" clearable
            :placeholder="$t('supplierCouponBuy.placeholder.couponId')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.name" clearable
            :placeholder="$t('supplierCouponBuy.placeholder.name')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.supplierName" clearable
            :placeholder="$t('supplierCouponBuy.placeholder.supplierName')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.storeName" clearable
            :placeholder="$t('supplierCouponBuy.placeholder.storeName')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.objName" clearable
            :placeholder="$t('supplierCouponBuy.placeholder.objName')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.createUserTel" clearable
            :placeholder="$t('supplierCouponBuy.placeholder.createUserTel')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.createUserName" clearable
            :placeholder="$t('supplierCouponBuy.placeholder.createUserName')" @keyup.enter.native="handleSearch" />
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="handleReset">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="getList">
        <template #left>
          <span class="ds-panel__title">{{ $t('supplierCouponBuy.listTitle') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('supplierCouponBuy.page.empty')" @retry="getList">
        <el-table-column prop="buyId" :label="$t('supplierCouponBuy.table.buyId')" align="center" width="140">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.buyId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="couponName" :label="$t('supplierCouponBuy.table.couponName')" align="left" min-width="160" show-overflow-tooltip />
        <el-table-column prop="name" :label="$t('supplierCouponBuy.table.name')" align="center" min-width="140" show-overflow-tooltip />
        <el-table-column prop="supplierName" :label="$t('supplierCouponBuy.table.supplierName')" align="center" min-width="140" show-overflow-tooltip />
        <el-table-column prop="quantity" :label="$t('supplierCouponBuy.table.quantity')" align="right" width="100">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.quantity }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="valuePrice" :label="$t('supplierCouponBuy.table.valuePrice')" align="right" width="110">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.valuePrice }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="storeName" :label="$t('supplierCouponBuy.table.storeName')" align="center" min-width="130" show-overflow-tooltip />
        <el-table-column prop="objName" :label="$t('supplierCouponBuy.table.objName')" align="center" min-width="130" show-overflow-tooltip />
        <el-table-column prop="createUserTel" :label="$t('supplierCouponBuy.table.createUserTel')" align="center" width="140">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.createUserTel || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="createUserName" :label="$t('supplierCouponBuy.table.createUserName')" align="center" width="120" />
        <el-table-column prop="remark" :label="$t('supplierCouponBuy.table.remark')" align="center" min-width="140" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.remark || '-' }}
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 30, 50]"
        @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>
  </div>
</template>

<script>
import { getSupplierCouponBuyList } from '@/api/scm/supplierCouponBuyApi'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'SupplierCouponBuyList',
  components: {
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        buyId: '',
        couponId: '',
        name: '',
        supplierName: '',
        storeName: '',
        objName: '',
        createUserTel: '',
        createUserName: ''
      },
      tableData: [],
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.getList()
  },
  methods: {
    async getList() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          ...this.searchForm,
          page: this.page.current,
          row: this.page.size
        }
        const { data, total } = await getSupplierCouponBuyList(params)
        this.tableData = Array.isArray(data) ? data : []
        this.page.total = total || 0
      } catch (error) {
        console.error('获取优惠券购买记录失败:', error)
        this.hasError = true
        this.errorMessage = error.message || this.$t('supplierCouponBuy.page.loadFailed')
        this.$message.error(this.$t('supplierCouponBuy.fetchError'))
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page.current = 1
      this.getList()
    },
    handleReset() {
      this.searchForm = {
        buyId: '',
        couponId: '',
        name: '',
        supplierName: '',
        storeName: '',
        objName: '',
        createUserTel: '',
        createUserName: ''
      }
      this.page.current = 1
      this.getList()
    },
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this.getList()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this.getList()
    }
  }
}
</script>

<style lang="scss" scoped>
.supplier-coupon-buy-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}
</style>
